import { nanoid } from "nanoid";
import type { MotiveDocument, ParameterDefinition } from "./types";
import { APP_VERSION, makeEmptyDocument } from "./types";

const parameter = (
  name: string,
  label: string,
  value: string,
  minimum: number,
  maximum: number,
  step = 1
): ParameterDefinition => ({ id: nanoid(), name, label, type: "length", value, minimum, maximum, step });

export function deskHookTemplate(): MotiveDocument {
  const doc = makeEmptyDocument("Desk Hook");
  doc.metadata.description = "A configurable hook that clips over the edge of a desk.";
  const desk = parameter("deskThickness", "Desk thickness", "28", 10, 80, 0.5);
  const wall = parameter("wallThickness", "Wall thickness", "4", 2, 12, 0.5);
  const depth = parameter("hookDepth", "Hook depth", "45", 15, 100, 1);
  const width = parameter("hookWidth", "Hook width", "28", 12, 80, 1);
  doc.parameters = Object.fromEntries([desk, wall, depth, width].map((item) => [item.id, item]));

  const outerId = nanoid();
  const innerId = nanoid();
  const cutId = nanoid();
  doc.nodes = [
    {
      id: outerId,
      type: "box",
      name: "Hook blank",
      enabled: true,
      visible: true,
      color: "#73d8b4",
      inputNodeIds: [],
      width: "hookWidth",
      depth: "hookDepth + wallThickness",
      height: "deskThickness + wallThickness * 2",
      centered: true,
      placement: { position: ["0", "0", "0"], rotation: ["0", "0", "0"] }
    },
    {
      id: innerId,
      type: "box",
      name: "Desk clearance",
      enabled: true,
      visible: true,
      color: "#e58c64",
      inputNodeIds: [],
      width: "hookWidth + 2",
      depth: "hookDepth",
      height: "deskThickness",
      centered: true,
      placement: { position: ["0", "wallThickness", "wallThickness"], rotation: ["0", "0", "0"] }
    },
    {
      id: cutId,
      type: "boolean",
      name: "Open hook",
      enabled: true,
      visible: true,
      color: "#73d8b4",
      inputNodeIds: [outerId, innerId],
      operation: "subtract"
    }
  ];
  doc.exposedParameters = [
    { parameterId: desk.id, label: desk.label, group: "Fit", minimum: 10, maximum: 80, step: 0.5 },
    { parameterId: depth.id, label: depth.label, group: "Size", minimum: 15, maximum: 100, step: 1 },
    { parameterId: width.id, label: width.label, group: "Size", minimum: 12, maximum: 80, step: 1 },
    { parameterId: wall.id, label: wall.label, group: "Strength", minimum: 2, maximum: 12, step: 0.5 }
  ];
  doc.applicationVersion = APP_VERSION;
  return doc;
}

export function mountingPlateTemplate(): MotiveDocument {
  const doc = makeEmptyDocument("Mounting Plate");
  doc.metadata.description = "A rectangular mounting plate with a four-hole circular pattern.";
  const plateWidth = parameter("plateWidth", "Plate width", "100", 30, 250, 1);
  const plateDepth = parameter("plateDepth", "Plate depth", "60", 20, 200, 1);
  const thickness = parameter("thickness", "Thickness", "5", 2, 20, 0.5);
  const holeRadius = parameter("holeRadius", "Hole radius", "3", 1, 10, 0.25);
  const holeSpacing = parameter("holeSpacing", "Hole spacing", "38", 10, 100, 1);
  doc.parameters = Object.fromEntries([plateWidth, plateDepth, thickness, holeRadius, holeSpacing].map((item) => [item.id, item]));

  const plateId = nanoid();
  const holeOneId = nanoid();
  const holeTwoId = nanoid();
  const holeThreeId = nanoid();
  const holeFourId = nanoid();
  doc.nodes = [
    {
      id: plateId,
      type: "box",
      name: "Plate",
      enabled: true,
      visible: true,
      color: "#73d8b4",
      inputNodeIds: [],
      width: "plateWidth",
      depth: "plateDepth",
      height: "thickness",
      centered: true,
      placement: { position: ["0", "0", "0"], rotation: ["0", "0", "0"] }
    },
    {
      id: holeOneId,
      type: "hole",
      name: "Top-right hole",
      enabled: true,
      visible: true,
      color: "#73d8b4",
      inputNodeIds: [plateId],
      radius: "holeRadius",
      depth: "thickness + 2",
      position: ["holeSpacing / 2", "holeSpacing / 2", "-1"],
      axis: "Z"
    },
    {
      id: holeTwoId,
      type: "hole",
      name: "Top-left hole",
      enabled: true,
      visible: true,
      color: "#73d8b4",
      inputNodeIds: [holeOneId],
      radius: "holeRadius",
      depth: "thickness + 2",
      position: ["-holeSpacing / 2", "holeSpacing / 2", "-1"],
      axis: "Z"
    },
    {
      id: holeThreeId,
      type: "hole",
      name: "Bottom-right hole",
      enabled: true,
      visible: true,
      color: "#73d8b4",
      inputNodeIds: [holeTwoId],
      radius: "holeRadius",
      depth: "thickness + 2",
      position: ["holeSpacing / 2", "-holeSpacing / 2", "-1"],
      axis: "Z"
    },
    {
      id: holeFourId,
      type: "hole",
      name: "Bottom-left hole",
      enabled: true,
      visible: true,
      color: "#73d8b4",
      inputNodeIds: [holeThreeId],
      radius: "holeRadius",
      depth: "thickness + 2",
      position: ["-holeSpacing / 2", "-holeSpacing / 2", "-1"],
      axis: "Z"
    }
  ];
  doc.exposedParameters = [
    { parameterId: plateWidth.id, label: plateWidth.label, group: "Plate", minimum: 30, maximum: 250, step: 1 },
    { parameterId: plateDepth.id, label: plateDepth.label, group: "Plate", minimum: 20, maximum: 200, step: 1 },
    { parameterId: thickness.id, label: thickness.label, group: "Plate", minimum: 2, maximum: 20, step: 0.5 },
    { parameterId: holeRadius.id, label: holeRadius.label, group: "Holes", minimum: 1, maximum: 10, step: 0.25 },
    { parameterId: holeSpacing.id, label: holeSpacing.label, group: "Holes", minimum: 10, maximum: 100, step: 1 }
  ];
  return doc;
}

export const bundledTemplates = [
  { id: "desk-hook", title: "Desk Hook", description: "Parametric clip-on desk hook", create: deskHookTemplate },
  { id: "mounting-plate", title: "Mounting Plate", description: "Configurable plate with mirrored holes", create: mountingPlateTemplate }
] as const;
